<script lang="ts" setup>
import { computed } from "#imports";
import MPViewportSpill from "../layouts/MPViewportSpill.vue";
const selectedCategories = defineModel<string[]>();

interface Category {
  name: string;
  icon: string | null;
  id: string;
}

const { categories, selectedFromFilter } = defineProps<{
  categories: Array<Category>;
  selectedFromFilter: string[];
}>();

const categoryOptions = computed<Array<Category>>(() => categories);

const emit = defineEmits<{
  click: [id: string];
}>();
</script>

<template>
  <div class="category-filter">
    <MPViewportSpill showInlineControl class="category-wrapper">
      <TransitionGroup tag="ul" name="list">
        <li
          v-for="category in categoryOptions"
          :key="category.id"
          hydrate-on-visible
        >
          <a
            :class="[
              'category-item',
              {
                active: selectedFromFilter
                  ? selectedFromFilter.includes(category.id)
                  : selectedCategories?.includes(category.id),
              },
            ]"
            @click="emit('click', category.id)"
          >
            <i v-if="category.icon" :class="category.icon" />
            {{ category.name }}
            <i class="ri-close-line close" />
          </a>
        </li>
      </TransitionGroup>
    </MPViewportSpill>
  </div>
</template>

<style lang="postcss" scoped>
@import "assets/css/breakpoints.pcss";
@import "assets/css/scrollbar.pcss";
.category-filter {
  display: grid;
  border-radius: 4rem;
  @mixin screen md {
    overflow: hidden;
    margin-right: 0.5rem;
    border-radius: 4rem;
  }
}

ul {
  max-width: 100vw;
  overflow-x: scroll;
  @mixin scrollbar-reset;
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  gap: 0.5rem;
  padding: 0;
  @mixin screen md {
    padding: 0;
    gap: 0.5rem;
  }
}
.category-item {
  display: flex;
  gap: 0.25rem;
  background-color: white;
  white-space: nowrap;
  padding: 0.35rem 0.75rem;
  border-radius: 1rem;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--color-slate-200);
  cursor: pointer;
  @mixin screen md {
    border-radius: 1.25rem;
    border: 1px solid var(--color-slate-100);
    box-shadow: var(--shadow-base);
  }
}
i {
  color: var(--color-slate-700);
}
.category-item.active {
  background-color: var(--color-slate-700);
  color: var(--color-slate-50);
  transition: all 0.1s ease;
  border: 1px solid var(--color-slate-700);
}
.category-item.active i {
  color: var(--color-slate-100);
  transition: all 0.1s ease;
}

.category-item i.close {
  width: 0;
  height: 0;
  visibility: hidden;
  transition: all 0.1s ease;
}

.category-item.active:hover i.close {
  width: 1rem;
  height: 1rem;
  visibility: visible;
  color: var(--color-slate-50);
  transition: all 0.1s ease;
}

.list-move,
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.list-leave-active {
  position: absolute;
}
</style>
